/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import TimeAgo from 'react-timeago';
import { decorate, action, computed, runInAction, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Checkbox, Icon, Popup } from 'semantic-ui-react';
import c from 'classnames';
import { disableStudyUploadByResearcher } from '../../helpers/settings';
import { displayError, displaySuccess } from '@amzn/base-ui/dist/helpers/notification';
import { updateStudy } from '../../helpers/api';

import StudyFilesTable from './StudyFilesTable';
import StudyPermissionsTable from './StudyPermissionsTable';
import UploadStudyFiles from './UploadStudyFiles';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - study (via props)
// - isSelectable (via props)
// - filesSelection (via injection)
// - userStore (via injection)
class StudyRow extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.filesExpanded = false;
      this.permissionsExpanded = false;
      this.configExpanded = false;
      this.updatingLicense = false;
    });
  }

  get getUserRole() {
    return this.props.userRole;
  }

  get study() {
    return this.props.study;
  }

  get isSelectable() {
    return this.props.isSelectable && this.study.state.canSelect;
  }

  get isAdmin() {
    return this.props.userStore?.user?.isAdmin || false;
  }

  get showAsResearcher() {
    return this.isAdmin && this.props.userStore.researcherView;
  }

  get showAdminConfig() {
    return this.isAdmin && !this.showAsResearcher && this.study.isOrganizationStudy;
  }

  handleFileSelection = () => {
    const study = this.study;
    const selection = this.props.filesSelection;
    if (selection.hasFile(study.id)) {
      selection.deleteFile(study.id);
    } else {
      const { id, name, description } = study;
      selection.setFile({ id, name, description, accessStatus: 'approved' });
    }
  };

  handleFilesExpanded = (e) => {
    e.stopPropagation();
    this.filesExpanded = !this.filesExpanded;
  };

  handlePermissionsExpanded = (e) => {
    e.stopPropagation();
    this.permissionsExpanded = !this.permissionsExpanded;
  };

  handleConfigExpanded = (e) => {
    e.stopPropagation();
    this.configExpanded = !this.configExpanded;
  };

  handleToggleLicense = async () => {
    const study = this.study;
    const newValue = !study.isLicense;
    try {
      runInAction(() => { this.updatingLicense = true; });
      await updateStudy(study.id, { rev: study.rev, isLicense: newValue });
      runInAction(() => {
        study.setStudy({ ...study, isLicense: newValue, rev: (study.rev || 0) + 1 });
      });
      displaySuccess(newValue ? 'Study marked as license' : 'Study unmarked as license');
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => { this.updatingLicense = false; });
    }
  };

  render() {
    const isSelectable = this.isSelectable;
    const study = this.study;
    const selection = this.props.filesSelection;
    const isSelected = selection.hasFile(study.id);

    return (
      <div className={c('study-card', { 'study-card-selected': isSelectable && isSelected })}>
        <div className="study-card-header">
          {isSelectable && (
            <div className="study-card-checkbox">
              <Checkbox checked={isSelected} onChange={this.handleFileSelection} />
            </div>
          )}
          <div className="study-card-body">
            {this.renderStatus(study.state)}
            {this.renderHeader(study, isSelectable)}
            {this.renderDescription(study)}
            {this.renderAccordions(study)}
          </div>
          <div className="study-card-actions">
            {this.renderUploadButton(study)}
          </div>
        </div>
      </div>
    );
  }

  renderHeader(study, isSelectable) {
    const onClickAttr = {};
    if (isSelectable) onClickAttr.onClick = this.handleFileSelection;

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          {this.renderCategoryPill(study)}
          <h3
            className={c('study-card-title', { 'study-card-title-clickable': isSelectable })}
            {...onClickAttr}
          >
            {study.name}
          </h3>
        </div>
        <div className="study-card-meta">
          <span className="study-card-meta-item">
            <Icon name="hashtag" style={iconStyle} />
            {study.id}
          </span>
          {study.projectId && (
            <span className="study-card-meta-item">
              <Icon name="folder outline" style={iconStyle} />
              {study.projectId}
            </span>
          )}
          {study.createdAt && (
            <span className="study-card-meta-item">
              <Icon name="clock outline" style={iconStyle} />
              Added <TimeAgo date={study.createdAt} />
            </span>
          )}
        </div>
      </div>
    );
  }

  // Kept in sync with StudySelectionGrid.getStudyIcon so the Data page and the workspace
  // creation Select Studies step show the same icon for a given study category.
  getStudyIcon(study) {
    if (study.isOpenDataStudy) return 'globe';
    if (study.isLicenseStudy) return 'key';
    if (study.isOrganizationStudy) return 'users';
    return 'folder';
  }

  renderCategoryPill(study) {
    const icon = this.getStudyIcon(study);

    if (study.isOpenDataStudy) {
      return (
        <Popup
          trigger={
            <span className="study-pill study-pill-open">
              <Icon name={icon} style={iconStyle} />
              Open Data
            </span>
          }
          content="This is an open dataset available to all users"
          size="tiny"
          inverted
          position="top center"
        />
      );
    }

    if (study.isLicenseStudy) {
      return (
        <Popup
          trigger={
            <span className="study-pill study-pill-license">
              <Icon name={icon} style={iconStyle} />
              License
            </span>
          }
          content="This study contains license activation data"
          size="tiny"
          inverted
          position="top center"
        />
      );
    }

    const isShared = study.isOrganizationStudy;
    const label = isShared ? 'Shared' : 'Personal';
    const tooltip = isShared
      ? 'This study has been shared with you'
      : 'This study is only accessible by you';
    const pillClass = isShared ? 'study-pill study-pill-shared' : 'study-pill study-pill-personal';

    return (
      <Popup
        trigger={
          <span className={pillClass}>
            <Icon name={icon} style={iconStyle} />
            {label}
          </span>
        }
        content={tooltip}
        size="tiny"
        inverted
        position="top center"
      />
    );
  }

  renderDescription(study) {
    if (!study.description) return null;
    return <div className="study-card-description">{study.description}</div>;
  }

  renderStatus(state) {
    if (state && (state.key === 'default' || state.key === 'reachable')) return null;

    return (
      <div style={{ marginBottom: '8px' }}>
        <Popup
          trigger={
            <span className="study-card-status" style={{ background: '#f3f4f6', color: '#6b7280' }}>
              {state.spinner && <Icon name="spinner" loading style={iconStyle} />}
              {state.display}
            </span>
          }
        >
          {state.tip}
        </Popup>
      </div>
    );
  }

  renderUploadButton(study) {
    const disableResearcherAccess = this.getUserRole === 'researcher' && disableStudyUploadByResearcher === true;
    if (!study.uploadLocationEnabled || !study.canUpload || disableResearcherAccess) return null;

    return <UploadStudyFiles studyId={study.id} />;
  }

  renderAccordions(study) {
    const isReadOnlyLicense = study.isLicenseStudy && !study.access.includes('admin') && !study.access.includes('readwrite');
    const showFiles = !study.isOpenDataStudy && study.uploadLocationEnabled && !isReadOnlyLicense;
    const showPermissions = study.isOrganizationStudy && this.study.isEditable;
    const showConfig = this.showAdminConfig;

    if (!showFiles && !showPermissions && !showConfig) return null;

    return (
      <div className="study-accordion">
        {showFiles && (
          <button
            className={c('study-accordion-trigger', { expanded: this.filesExpanded })}
            onClick={this.handleFilesExpanded}
          >
            <Icon name="caret right" style={iconStyle} />
            Files
          </button>
        )}
        {showPermissions && (
          <button
            className={c('study-accordion-trigger', { expanded: this.permissionsExpanded })}
            onClick={this.handlePermissionsExpanded}
          >
            <Icon name="caret right" style={iconStyle} />
            Permissions
          </button>
        )}
        {showConfig && (
          <button
            className={c('study-accordion-trigger', { expanded: this.configExpanded })}
            onClick={this.handleConfigExpanded}
          >
            <Icon name="caret right" style={iconStyle} />
            Configuration
          </button>
        )}
        {this.filesExpanded && showFiles && (
          <div className="study-accordion-content">
            <StudyFilesTable study={study} />
          </div>
        )}
        {this.permissionsExpanded && showPermissions && (
          <div className="study-accordion-content">
            <StudyPermissionsTable study={study} />
          </div>
        )}
        {this.configExpanded && showConfig && (
          <div className="study-accordion-content">
            {this.renderConfigContent(study)}
          </div>
        )}
      </div>
    );
  }

  renderConfigContent(study) {
    return (
      <div className="study-config-panel">
        <div className="study-config-row">
          <div className="study-config-label">
            <span className="study-config-label-text">License Study</span>
            <span className="study-config-label-hint">Mark this study as a license. It will appear under the Licenses tab and in the Licenses section during workspace creation.</span>
          </div>
          <Checkbox
            toggle
            checked={study.isLicense}
            disabled={this.updatingLicense}
            onChange={this.handleToggleLicense}
          />
        </div>
      </div>
    );
  }
}

decorate(StudyRow, {
  handleFileSelection: action,
  handleFilesExpanded: action,
  handlePermissionsExpanded: action,
  handleConfigExpanded: action,
  handleToggleLicense: action,
  study: computed,
  isAdmin: computed,
  showAsResearcher: computed,
  showAdminConfig: computed,
  filesExpanded: observable,
  permissionsExpanded: observable,
  configExpanded: observable,
  updatingLicense: observable,
  isSelectable: computed,
});

export default inject('filesSelection', 'userStore')(observer(StudyRow));
